<template>
  <div class="heatmap__control-bar">
    <div class="heatmap__control-bar-top">
      <!-- device -->
      <div class="heatmap__control__device" :class="{ 'mobile-only': bMobile }">
        <button v-for="item in deviceList" :key="item"
          class="item" :class="{ on: device === item }"
          @click="$emit('onDeviceChange', item)">
          <i class="icon" :class="`icon--device-${item}`"></i>
          <template v-if="bMobile">
            <span class="text device_name">{{ $t('common.deviceType.phone') }}</span>
            <span class="delimiter"></span>
          </template>
          <span class="text percentage" v-if="infoData">{{ getDevicePvPercentage(item) | fixed(1) }}%</span>
          <span class="text percentage" v-else>0%</span>
        </button>
      </div>
      <!-- referrer -->
      <div class="clearfix heatmap__control__referrer">
        <span class="panel__title">{{ $t('heatmap.referrer') }}</span>
        <div class="referrer--wrap">
          <p class="selected-referrer"
             @click.stop="toggleReferrerList">
            <span>{{ referer === 'all' ? $t('common.all') : referer }}</span>
            <template v-if="referer !== 'all' && infoData">
              ({{ getRefererPvPercentage(referer) | fixed(1) }}%)
            </template>
            <i class="icon icon--drop-down"></i>
          </p>
          <transition name="slide">
            <div class="referrer__list--wrap"
                 :class="{'is-show': showReferrerList}"
                 v-show="showReferrerList">
              <ul class="referrer__list">
                <li class="referrer__list-item"
                    :class="referer === 'all' ? 'on' : ''"
                    :title="$t('common.all')"
                    @click="onRefererChange('all')">{{ $t('common.all') }}</li>
                <template v-if="infoData">
                  <li class="referrer__list-item"
                      v-for="(item, index) in refererData" :key="index"
                      :class="referer === item.refer ? 'on' : ''"
                      :title="item.refer"
                      @click="onRefererChange(item.refer)">
                    <span class="referrer-name" :title="item.refer">{{ item.refer }}</span>
                    ({{ getRefererPvPercentage(item.refer) | fixed(1) }}%)
                  </li>
                </template>
              </ul>
            </div>
          </transition>
        </div>
      </div>
    </div>

    <div class="option_area heatmap__control-bar-bottom">
      <!-- new / return -->
      <div class="heatmap__control__play">
        <span v-for="item in eventSourceButtons" :key="item.key"
              class="checkbox_wrap"
              :class="getEventSourceClass(item.key)"
              @click="setEventSource(item.key)">
          <i class="icon icon--chkbox"></i>
          <span class="text">{{ $t(item.t) }}</span>
        </span>
      </div>

      <!-- type -->
      <div class="heatmap__control__type" :class="[`size${getTypeList.length}`, {'amp' : applyData.bAMP}]">
        <div class="panel_inner_wrap">
          <div class="button_set">
            <button v-for="item in getTypeList" :key="item"
              class="item" :class="{ on: eventType === item }"
              @click="$emit('onEventTypeChange', item)">
              <span class="text">{{ $t(`heatmap.panel.${item}`) }}</span>
            </button>
          </div>
        </div>
      </div>

      <div class="heatmap__control__partition"></div>

      <!-- LiveHeatmap -->
      <div class="heatmap__control__liveheatmap">
        <a class="liveheatmap-btn" target="_blank" href="#"
          :class="{ disabled: !canViewLiveHeatmap }"
          @click.prevent="openLiveHeatmap">
          <!-- <i class="icon icon--liveHeatmap"></i>
          <span class="text">{{ $t('heatmap.liveHeatmap') }}</span> -->
        </a>
      </div>

      <!-- total -->
      <div class="heatmap__control__total">
        <ul>
          <li>
            {{ metric.pv | thousand }} <span class="unit-pv">PV</span>
          </li>
        </ul>
      </div>
    </div>
  </div>
</template>

<script>
import store from '@/store'
import { mapState } from 'vuex'
import { EventBus } from '@/libs/event-bus'
import { getPercentage, triggerClickEvent } from '@/libs/utils'
import { getLiveHeatmapUrl } from '@/api/apply'
import { NEW_MOBILE_HEATMAP_STARTDATE } from '@/libs/constants'

// const EVENT_SOURCE_NONE = 0 // 0000
const EVENT_SOURCE_NEW = 1 // 0001
const EVENT_SOURCE_RETURN = 2 // 0011
const EVENT_SOURCE_ALL = 3 // 0011

const eventSourceButtons = [
  {
    key: 'new',
    t: 'heatmap.cvr_exit.new'
  },
  {
    key: 'return',
    t: 'heatmap.cvr_exit.revisit'
  }
]

export default {
  name: 'HeatmapControl',
  data () {
    return {
      showReferrerList: false
    }
  },
  props: [
    'mode',
    'aid',
    'infoData',
    'referer',
    'device',
    'eventType',
    'eventSource',
    'applyData',
    'metric'
  ],
  computed: {
    ...mapState([
      'userData'
    ]),
    reportPeriod () {
      return store.state.reportPeriod
    },
    getTypeList () {
      let typelist = ['click', 'move', 'scroll', 'stream']
      if (this.device !== 'desktop') {
        typelist = ['tap', 'zoom', 'swipe', 'scroll', 'stream']

        if (this.reportPeriod.start < NEW_MOBILE_HEATMAP_STARTDATE) {
          typelist = ['click', 'move', 'scroll', 'stream']
        }
      }
      if (this.applyData.bAMP) {
        typelist = ['click', 'scroll', 'stream']
      }
      return typelist
    },
    deviceData () {
      return this.infoData.device[this.device]
    },
    refererData () {
      if (this.deviceData) {
        return this.deviceData.refer
      }
      return null
    },
    eventSourceFlag () {
      switch (this.eventSource) {
        case 'new':
          return EVENT_SOURCE_NEW
        case 'return':
          return EVENT_SOURCE_RETURN
        case 'all':
          return EVENT_SOURCE_ALL
        default:
          throw new Error(`invalid eventSource: ${this.eventSource}`)
      }
    },
    flagNew () {
      return this.eventSourceFlag & EVENT_SOURCE_NEW
    },
    flagReturn () {
      return this.eventSourceFlag & EVENT_SOURCE_RETURN
    },
    bMobile () {
      return this.applyData.bMobile
    },
    deviceList () {
      if (this.bMobile) {
        return ['phone']
      } else {
        return ['desktop', 'tablet', 'phone']
      }
    },
    canViewLiveHeatmap () {
      return ['default', 'cta'].includes(this.mode) &&
        ['exact', 'simple'].includes(this.applyData.matchType)
    }
  },
  watch: {
    showReferrerList (value) {
      let clickHandler = () => {
        this.showReferrerList = false
      }

      if (value) {
        document.body.addEventListener('click', clickHandler, false)
        this.$nextTick().then(() => {
          const activeEl = this.$el.querySelector('.referrer__list-item.on')

          if (activeEl) {
            activeEl.parentNode.scrollTop = activeEl.offsetTop
          }
        })
      } else {
        document.body.removeEventListener('click', clickHandler)
      }
    }
  },
  created () {
    this.eventSourceButtons = eventSourceButtons
  },
  mounted () {
    EventBus.$on('closeReferrerList', () => {
      this.showReferrerList = false
    })
  },
  methods: {
    setEventSource (source) {
      let flag

      if (source === 'new') {
        flag = this.flagReturn | (this.flagNew ^ EVENT_SOURCE_NEW)
      } else {
        flag = this.flagNew | (this.flagReturn ^ EVENT_SOURCE_RETURN)
      }

      if (!flag) {
        flag = source === 'new' ? EVENT_SOURCE_RETURN : EVENT_SOURCE_NEW
      }

      let newSource

      switch (flag) {
        case EVENT_SOURCE_NEW:
          newSource = 'new'
          break
        case EVENT_SOURCE_RETURN:
          newSource = 'return'
          break
        case EVENT_SOURCE_ALL:
          newSource = 'all'
          break
      }
      this.$emit('onEventSourceChange', newSource)
    },
    toggleReferrerList () {
      EventBus.$emit('closeAllUiSelectBox')
      EventBus.$emit('closeUserMenu')
      EventBus.$emit('closeDateRangePicker')
      EventBus.$emit('closeReportPageList')
      if (!this.showReferrerList) {
        triggerClickEvent()
      }
      this.showReferrerList = !this.showReferrerList
    },
    getEventSourceClass (source) {
      // guide
      let value = source === 'new' ? this.flagNew : this.flagReturn
      return { on: !!value }
    },
    getDevicePvPercentage (device) {
      if (!this.infoData.device[device]) {
        return 0
      }
      return getPercentage(this.infoData.device[device].pv, this.infoData.pv)
    },
    getRefererPvPercentage (referer) {
      const item = this.refererData.find(item => item.refer === referer)

      if (!item) {
        return 0
      }
      return getPercentage(item.pv, this.deviceData.pv)
    },
    onRefererChange (value) {
      this.$emit('onRefererChange', value)
      this.isReferrerList = false
    },
    openLiveHeatmap () {
      getLiveHeatmapUrl(this.aid).then(data => {
        window.open(data.url + '?beusably_lang=' + this.$i18n.locale)
      }).catch(err => {
        console.error(err)
      })
    }
  }
}
</script>

<style lang="scss">
.heatmap__control-bar {

  .heatmap__control-bar-top {
    height: 62px;
    box-sizing: border-box;
    border-bottom: 1px solid #ebebeb;
    background-color: #fff;
    display: flex;
    justify-content: space-between;
    align-items: center;
    border-top-left-radius: 12px;
    border-top-right-radius: 12px;
    padding-right: 15px;

    .heatmap__control__device{
      position: relative;
      white-space: nowrap;
      display: inline-block;
      width: 261px;
      height: 38px;
      border-radius: 20px;
      border: solid 1px #e0e0e0;
      background-color: #ffffff;
      box-sizing: border-box;
      margin-left: 15px;

      button {
        position: absolute;
        top: 0;
        width: 91px;
        height: 100%;
        border-radius: 20px;
        background-color: transparent;

        &> span {
          vertical-align: middle;
        }

        &:hover:not(.on) .text {
          color: #444;
        }

        &:nth-of-type(1) {
          left: 0;
          &.on {
            left: -1px;
          }
          &:hover:not(.on) i.icon--device-desktop {
            background-image: url(../../assets/img/heatmap/heatmap_device_desktop_hover.svg);
          }
        }

        &:nth-of-type(2) {
          left: 50%;
          transform: translateX(-50%);
          &.on {
            top: -1px;
          }
          &:hover:not(.on) i.icon--device-tablet  {
            background-image: url(../../assets/img/heatmap/heatmap_device_tablet_hover.svg);
          }
        }

        &:nth-of-type(3) {
          right: 0;
          &.on {
            right: -1px;
          }
          &:hover:not(.on) i.icon--device-phone {
            background-image: url(../../assets/img/heatmap/heatmap_device_phone_hover.svg);
          }
        }

        &:focus {
          z-index: 1;
        }

        &.on {
          top: -1px;
          height: 38px;
          box-shadow: 0 12px 10px -4px rgba(0, 0, 0, 0.2);
          background-color: $brandColor;
          z-index: 2;
          pointer-events: none;
          .text {
            color: #fff;
          }
        }

        i {
          vertical-align: middle;
          margin-right: 2px;
        }
        .text {
          font-size: 13px;
          font-weight: 500;
          color: #888888;
        }
        .percentage {
          display: inline-block;
          width: 42px;
          text-align: center;
        }
        .device_name {
          margin-left: 5px;
        }
        .delimiter {
          display: inline-block;
          width: 1px;
          height: 11px;
          background-color: #fff;
          opacity: 0.5;
          margin-right: 4px;
          margin-left: 8px;
        }
      }

      &.mobile-only {
        width: 156px;
        button {
          width: 156px;
        }
      }
    }

    .heatmap__control__referrer {
      display: inline-block;

      .panel__title {
        font-size: 14px;
        font-weight: 500;
        color: #767676;
        line-height: 38px;
        margin-right: 10px;
        display: inline-block;
        height: 100%;
        vertical-align: top;
      }

      .referrer--wrap {
        position: relative;
        padding-left: 15px;
        display: inline-block;
        width: 370px;
        height: 38px;
        line-height: 36px;
        border-radius: 6px;
        border: solid 1px #bbbbbb;
        background-color: #ffffff;
        box-sizing: border-box;
        font-size: 14px;
        color: #2f2f2f;

        .selected-referrer {
          position: relative;
          color: $brandColor;

          &> span {
            display: inline-block;
            max-width: 265px;
            white-space: nowrap;
            text-overflow: ellipsis;
            overflow: hidden;
            vertical-align: bottom;
          }

          .icon {
            pointer-events: none;
            position: absolute;
            top: 50%;
            right: 12px;
            transform: translateY(-50%);
          }
        }

        .referrer__list--wrap {
          width: 370px;
          overflow: hidden;
          position: absolute;
          top: -1px;
          left: -1px;
          background-color: #fff;
          z-index: 9999999;
          border-radius: 6px;
          box-shadow: 0 23px 15px -12px rgba(0, 0, 0, 0.08);
          box-sizing: border-box;
          max-height: 320px;
          border: 1px solid rgba(0, 0, 0, 0.25);
          opacity: 0;
          transform-origin: top;
          transition: transform .2s ease, opacity .1s ease;

          &.is-show {
            opacity: 1;
          }

          .referrer__list {
            max-height: 320px;
            overflow-y: auto;

            .referrer__list-item {
              padding-left: 15px;
              height: 38px;
              color: #444;
              cursor: pointer;

              &.on {
                color: $brandColor;
                background-color: #f4f4f4;
              }

              &:hover {
                background-color: #ebebeb;
              }

              &:nth-of-type(1) {
                border-top-left-radius: 6px;
                border-top-right-radius: 6px;
              }

              .referrer-name {
                display: inline-block;
                max-width: 270px;
                text-overflow: ellipsis;
                white-space: nowrap;
                overflow: hidden;
                vertical-align: bottom;
              }
            }
          }
        }
      }
    }
  }

  .heatmap__control-bar-bottom {
    background-color: #fff;
    display: flex;
    align-items: center;
    height: 62px;
    position: relative;

    .heatmap__control__play {
      margin-left: 20px;
      white-space: nowrap;

      .checkbox_wrap {
        position: relative;
        cursor: pointer;

        .icon {
          position: absolute;
          top: 50%;
          left: 0;
          transform: translateY(-50%);
        }

        &:nth-last-of-type(1) {
          margin-left: 18px;
        }
      }

      .text {
        font-size: 14px;
        color: #2f2f2f;
        margin-left: 25px;
      }
    }

    .heatmap__control__type {
      position: relative;
      white-space: nowrap;
      display: inline-block;
      width: 245px;
      height: 38px;
      border-radius: 20px;
      border: solid 1px #e0e0e0;
      background-color: #ffffff;
      box-sizing: border-box;
      margin-left: 15px;

      & > div,
      & > div > div {
        height: 100%;
      }

      button {
        position: absolute;
        top: 0;
        width: 62px;
        height: 100%;
        border-radius: 20px;
        background-color: transparent;

        &:hover:not(.on) .text{
          color: #444;
        }

        &:hover[disabled] .text {
          color: #ccc;
        }

        &:nth-of-type(1) {
          left: 0;
          &.on {
            top: -1px;
            left: -1px;
          }
        }

        &:nth-of-type(2) {
          left: 25%;
          &.on {
            top: -1px;
          }
        }

        &:nth-of-type(3) {
          left: 50%;
          &.on {
            top: -1px;
          }
        }

        &:nth-of-type(4) {
          z-index: 1;
          left: 75%;
          overflow: visible;

          &.on {
            top: -1px;
            right: -1px;
          }
        }

        &:focus {
          z-index: 1;
        }

        &.on {
          height: 38px;
          box-shadow: 0 12px 10px -4px rgba(0, 0, 0, 0.2);
          background-color: $brandColor;
          pointer-events: none;
          .text {
            color: #fff;
          }
        }

        &[disabled] {
          cursor: default;
          .text {
            color: #ccc;
          }
        }

        .text {
          font-size: 13px;
          font-weight: 500;
          color: #888888;
          position: relative;
        }
      }

      &.size5 {
        width: 279px;

        button {
          width: 63px;

          &:nth-of-type(1) {
            width: 58px;
          }

          &:nth-of-type(2) {
            left: calc(20% - 4px);
          }

          &:nth-of-type(3) {
            left: calc(40% - 4px);
          }

          &:nth-of-type(4) {
            left: calc(60% - 4px);

            &.on {
              right: auto;
            }
          }

          &:nth-of-type(5) {
            left: calc(80% - 7px);

            &.on {
              top: -1px;
              right: -1px;
            }
          }
        }
      }

      .in-ready {
        top: -22px;
        position: absolute;
        @include positionXcenter;
        white-space: nowrap;
        box-shadow: 0 8px 15px 0 rgba(0, 0, 0, 0.16);
        background-color: #1199af;
        color: #fff;
        font-size: 12px;
        text-align: center;
        padding: 6px 10px;
        border-radius: 4px;

        &:after {
          content: '';
          background-color: #1199af;
          position: absolute;
          z-index: -1;
          width: 10px;
          height: 10px;
          bottom: 0px;
          left: 50%;
          transform: rotate(-45deg) translateX(-50%);
        }
      }

      .label__new {
        @include positionXcenter;
        white-space: nowrap;
        background-color: #FFAC00;
        color: #fff;
        font-weight: bold;
        text-align: center;
        border-radius: 4px;
        display: inline-block;
        text-transform: uppercase;
        top: -9px;
        font-size: 11px;
        padding: 0 4px;
        text-shadow: 0px 0px 1px rgba(97,0,0,0.25);
        height: 16px;
        line-height: 18px;
      }
    }

    .heatmap__control__partition {
      width: 1px;
      height: 20px;
      border-left: 1px solid #d7d7d7;
      margin-left: 10px;
      margin-right: 10px;
    }

    .heatmap__control__liveheatmap {

      .liveheatmap-btn {
        position: relative;
        display: inline-block;
        width: 141px;
        height: 38px;
        line-height: 36px;
        border-radius: 20px;
        border: solid 1px #bbbbbb;
        box-sizing: border-box;
        text-align: center;
        overflow: hidden;
        background: #fff url(../../assets/img/heatmap/live-heatmap-logo.svg) no-repeat top 48% left 52%;

        &:hover {
          &:after {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background-color: rgba(0, 0, 0, 0.06);
          }
        }

        &:active {
          &:after {
            background-color: rgba(0, 0, 0, 0.16);
          }
        }

        &.disabled {
          opacity: 0.4;
          pointer-events: none;
        }

        .icon {
          position: absolute;
          top: 50%;
          left: 6px;
          transform: translateY(-50%);
        }

        .text {
          font-size: 14px;
          font-weight: 500;
          color: #2f2f2f;
          margin-left: 25px;
        }
      }
    }

    .heatmap__control__total {
      position: absolute;
      top: 50%;
      right: 15px;
      transform: translateY(-50%);
      font-size: 12px;
      line-height: 1;
      text-align: right;
      color: #444444;

      li:nth-last-of-type(1) {
        margin-top: 2px;
      }

      .unit-pv {
        font-weight: bold;
        color: #ec0047;
      }
      .unit-uv {
        font-weight: bold;
        color: #43b6c9;
      }
    }
  }

  /* referrer content slide */
  .slide-enter, .slide-leave-to{
    transform: scaleY(0);
    opacity: 0;
  }
}

[lang='ja'] {
  .heatmap__control-bar .heatmap__control-bar-bottom .heatmap__control__type .label__new {
    font-size: 12px;
    height: 17px;
  }
}
</style>
